import React, { useState } from 'react'
import { dateTimeText, utcText } from '../../../shared/date/local-time'
import { useRoleOrg } from '../../../role/hook/role-org'
import { ROLE_ORG } from '../../../role/role.context'
import { failureText, lmsAdmin, readFailureText } from '../api'
import { LmsIcon } from '../lms-icon'

// What an LTI 1.3 connection's launches and grade returns ran into that its
// admin can fix (Phase L Task 8), from the connection's diagnostics:
//   - lti_refusals: why login initiations and launches were refused in the
//     last 30 days, by reason (the service's own sentence for each code), the
//     newest first. Never a value the LMS sent. Each count is an attempt (a
//     login or a launch), never a learner.
//   - lti_score_failures: the grade returns that failed (Task 7's outbox; the
//     same shape). The service always sends the list, [] when none failed. Under
//     it, "Resend failed grades" puts the failed grades back in the queue.
//   - lti_score_applied_409: how many grades the LMS answered "already applied"
//     (a 409) to. A 409 counts as sent, so they are not failures; the count sits
//     beside them because a platform that answers 409 to everything takes no
//     grade and shows only here, as a count that grows with the grades.

const sentence = text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '')

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`

// A resend sends again a grade that was queued. Two failures have none to send:
// no_grade_passback queued nothing, and no_line_item reads the launch's stored
// grade service claim again. Their grades go when each learner launches the
// activity again, after the activity accepts grades (the lms OpenAPI's
// lti_score_failures).
const RESEND_CANNOT = ['no_grade_passback', 'no_line_item']

const WAITS_FOR_LAUNCH = "Resend can't send these grades. Each one is sent when its learner launches the activity again, after the activity accepts grades."

// What to do about a reason, where there is something to do.
const SCORE_HINTS = {
  no_grade_passback: `To fix it, turn on IMS LTI Assignment and Grade Services for this tool in your LMS, and set "Accept grades from the tool" to Always (see the recommended settings above). ${WAITS_FOR_LAUNCH}`,
  no_line_item: `To fix it, set the tool's IMS LTI Assignment and Grade Services to "Use this service for grade sync and column management" in your LMS, or make a grade item for the activity (see the recommended settings above). ${WAITS_FOR_LAUNCH}`
}

const hasRowsResendCannotSend = rows => rows.some(row => RESEND_CANNOT.includes(row.code))
const hasRowsResendCanSend = rows => rows.some(row => !RESEND_CANNOT.includes(row.code))

// What the line above the button says Resend does, given the listed failures.
const resendHelp = rows => {
  if (!hasRowsResendCannotSend(rows)) return 'Sends the failed grades again. Do this after fixing the cause in your LMS.'
  if (hasRowsResendCanSend(rows)) return 'Sends the other failed grades again. Do this after fixing the cause in your LMS.'
  return 'Resend has nothing to send again for these grades. Fix the cause in your LMS: each is sent when its learner launches the activity again.'
}

const Rows = ({ id, rows, one, many, seen, hints = {} }) => (
  <ul className="list-unstyled mb-0" data-testid={id}>
    {rows.map(row => (
      <li key={row.code} data-testid={`${id.replace(/s$/, '')}-${row.code}`} style={{ padding: '4px 0' }}>
        <div>{sentence(row.reason) || row.code}</div>
        <div className="field-help" style={{ marginTop: 0 }}>
          {plural(row.count, one, many)} · {seen}{' '}
          <span title={utcText(row.last_seen_at)}>{dateTimeText(row.last_seen_at)}</span>
        </div>
        {hints[row.code] && (
          <div className="field-help" style={{ marginTop: 0 }} data-testid={`${id.replace(/s$/, '')}-hint-${row.code}`}>
            {hints[row.code]}
          </div>
        )}
      </li>
    ))}
  </ul>
)

// What a resend queued, in words. `withWaiting`: grades are listed that a resend
// cannot send. Those are not "sent once the LMS allows": nothing is queued for
// them, so they go only when their learners launch the activity again.
const requeuedText = (result, withWaiting) => {
  const count = result && result.requeued
  if (typeof count !== 'number') return 'The failed grades were queued to be sent again.'
  if (count === 0) {
    return withWaiting
      ? 'No grade could be queued again. Resend does not send grades that failed for want of a grade passback or a grade item. Each is sent when its learner launches the activity again, after the activity accepts grades.'
      : 'No grade could be queued again.'
  }
  return `${plural(count, 'grade', 'grades')} queued to be sent again.`
}

// "Resend failed grades": for the LMS admin scope (the service checks it too),
// never on an archived connection (it sends no grades). Drawn only when there
// are failures, so the role is read only then.
const ResendFailedGrades = ({ connectionId, archived, scoreFailures }) => {
  const lms = useRoleOrg(ROLE_ORG.LMS)
  const isLmsAdmin = !!lms.accessCreate && !!lms.accessDelete
  const [busy, updateBusy] = useState(false)
  const [result, updateResult] = useState(null)
  const [error, updateError] = useState(null)

  const resend = async () => {
    if (busy) return
    updateBusy(true)
    updateResult(null)
    updateError(null)
    try {
      updateResult(await lmsAdmin.resendLtiScores(connectionId))
    } catch (e) {
      updateError(e)
    } finally {
      updateBusy(false)
    }
  }

  const note = archived ? 'An archived connection sends no grades.' : !isLmsAdmin ? 'Resending needs the LMS administrator role.' : null
  const withWaiting = hasRowsResendCannotSend(scoreFailures)

  return (
    <div style={{ marginTop: 10 }}>
      <div className="field-help" style={{ marginTop: 0, marginBottom: 6 }} data-testid="lti-resend-help">
        {resendHelp(scoreFailures)}
      </div>
      <button
        className="btn btn-outline-secondary btn-sm"
        type="button"
        disabled={busy || !!note}
        data-testid="lti-resend"
        onClick={resend}
      >
        {busy ? 'Resending…' : 'Resend failed grades'}
      </button>
      {note && (
        <div className="field-help" data-testid="lti-resend-note">
          {note}
        </div>
      )}
      {result && (
        <div className="field-help" data-testid="lti-resend-result">
          <LmsIcon name="check" className="me-1" />
          {requeuedText(result, withWaiting)}
        </div>
      )}
      {error && (
        <div className="field-error" data-testid="lti-resend-error">
          <LmsIcon name="circle-exclamation" />
          {failureText(error, "The failed grades couldn't be queued again.")}
        </div>
      )}
    </div>
  )
}

// The grades the LMS said it already had (a 409), in words. Drawn only when
// there are some.
const AlreadyApplied = ({ count }) => (
  <div style={{ marginTop: 10 }} data-testid="lti-score-applied-409">
    <div>Grades the LMS already had: {count}</div>
    <div className="field-help" style={{ marginTop: 0 }}>
      In the last 30 days your LMS answered "already applied" to these. They count as sent. If this number grows with every
      grade, your LMS may be answering that to everything and taking no grade.
    </div>
  </div>
)

// `refusals` and `scoreFailures` are null while the diagnostics load; `error` is
// a failed read of them. `alreadyApplied` is the count of grades the LMS already
// had. `archived`: the connection sends no grades.
export const LtiDiagnostics = ({ connectionId, archived, refusals, scoreFailures, alreadyApplied = 0, error }) => (
  <div className="form-section" data-testid="lti-diagnostics">
    <div className="form-section-title">
      <span className="num">
        <LmsIcon name="triangle-exclamation" style={{ fontSize: 9 }} />
      </span>
      <h2 className="label">Refused LTI launches</h2>
    </div>
    <div className="field">
      {error ? (
        <div className="field-help" style={{ marginTop: 0 }} data-testid="lti-diagnostics-error">
          {readFailureText('The refused launches', error)}
        </div>
      ) : refusals === null ? (
        <div className="field-help" style={{ marginTop: 0 }}>Loading…</div>
      ) : refusals.length === 0 ? (
        <div className="field-help" style={{ marginTop: 0 }} data-testid="lti-refusals-none">
          No launch was refused in the last 30 days.
        </div>
      ) : (
        <React.Fragment>
          <div className="field-help" style={{ marginTop: 0, marginBottom: 6 }}>
            Logins and launches from your LMS that were refused in the last 30 days, by reason. Each count is an attempt, not a
            learner: a learner who tries again is counted again.
          </div>
          <Rows id="lti-refusals" rows={refusals} one="attempt" many="attempts" seen="last refused" />
        </React.Fragment>
      )}
    </div>

    <div className="form-section-title" style={{ marginTop: 14 }}>
      <span className="num">
        <LmsIcon name="list-check" style={{ fontSize: 9 }} />
      </span>
      <h2 className="label">Failed grade returns</h2>
    </div>
    <div className="field">
      {error ? (
        <div className="field-help" style={{ marginTop: 0 }} data-testid="lti-score-failures-error">
          {readFailureText('The failed grade returns', error)}
        </div>
      ) : scoreFailures === null ? (
        <div className="field-help" style={{ marginTop: 0 }}>Loading…</div>
      ) : scoreFailures.length === 0 ? (
        <div className="field-help" style={{ marginTop: 0 }} data-testid="lti-score-failures-none">
          No grade return failed in the last 30 days.
        </div>
      ) : (
        <React.Fragment>
          <div className="field-help" style={{ marginTop: 0, marginBottom: 6 }}>
            Grades EducationXR could not send to your LMS's gradebook, by reason. The learner's result in EducationXR is
            unchanged.
          </div>
          <Rows id="lti-score-failures" rows={scoreFailures} one="grade" many="grades" seen="last failed" hints={SCORE_HINTS} />
          <ResendFailedGrades connectionId={connectionId} archived={archived} scoreFailures={scoreFailures} />
        </React.Fragment>
      )}
      {!error && scoreFailures !== null && alreadyApplied > 0 && <AlreadyApplied count={alreadyApplied} />}
    </div>
  </div>
)
